import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Award,
  FileText,
  Upload,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ClipboardCheck,
  TrendingUp,
  Users,
  DollarSign,
  CheckCircle2,
  XCircle,
  FilePlus,
  ClipboardSignature,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { formatCurrency, formatDate } from '../utils/helpers';
import Badge from './common/Badge';
import Button from './common/Button';
import { t } from '../i18n';

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length);
  if (lines.length <= 1) return [];
  const headers = lines[0].split(',').map((header) => header.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(',').map((cell) => cell.trim());
    return headers.reduce((acc, header, index) => {
      acc[header] = cells[index] || '';
      return acc;
    }, {});
  });
};

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch (error) {
      // value is not valid JSON, fallback to comma-separated parsing
    }
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  return [value].filter(Boolean);
};

const normalizeContacts = (contacts) => {
  if (!Array.isArray(contacts)) return [];
  return contacts.map((entry, index) => {
    if (typeof entry === 'string') {
      return {
        id: `contact-${index}`,
        name: entry,
        role: '',
        mail: '',
        phone: ''
      };
    }
    return {
      id: entry.id || `contact-${index}`,
      name: entry.name || entry.contacto || 'Contacto',
      role: entry.role || entry.cargo || '',
      mail: entry.mail || entry.correo || '',
      phone: entry.phone || entry.telefono || ''
    };
  });
};

const checklistItems = [
  {
    key: 'tributariaVigente',
    label: 'Certificación tributaria al día',
    description: 'Revisión de antecedentes SII y obligaciones fiscales vigentes.'
  },
  {
    key: 'sinJuiciosSanciones',
    label: 'Sin juicios ni sanciones',
    description: 'Validación de antecedentes judiciales y sanciones registradas.'
  },
  {
    key: 'seguridadIndustrial',
    label: 'Cumplimiento seguridad industrial',
    description: 'Protocolos de seguridad y prevención de riesgos certificados.'
  },
  {
    key: 'sinAntecedentesCorrupcion',
    label: 'Sin antecedentes de corrupción',
    description: 'Validación de listas restrictivas y políticas anticorrupción.'
  }
];

const riskTones = {
  Bajo: 'success',
  Medio: 'warning',
  Alto: 'danger'
};

const viabilityTones = {
  aprobado: 'success',
  condicionado: 'warning',
  rechazado: 'danger',
  pendiente: 'neutral'
};

const viabilityLabels = {
  aprobado: 'Aprobado',
  condicionado: 'Condicionado',
  rechazado: 'Rechazado',
  pendiente: 'Pendiente'
};

const riskOptions = ['Bajo', 'Medio', 'Alto'];

const supplierStatusTones = {
  Approved: 'success',
  Rejected: 'danger',
  'Pending Approval': 'warning',
  'Pending Legal': 'neutral'
};

const reviewStatusLabels = {
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
  pendiente: 'Pendiente'
};

const reviewStatusTones = {
  aprobado: 'success',
  rechazado: 'danger',
  pendiente: 'neutral'
};

const SECTION_TABS = [
  {
    id: 'gestion',
    label: 'Gestión de proveedores',
    description: 'Alta, ficha y órdenes vinculadas',
    icon: Building2
  },
  {
    id: 'compliance',
    label: 'Compliance',
    description: 'Due diligence, checklist y documentos',
    icon: ShieldCheck
  },
  {
    id: 'homologacion',
    label: 'Homologación',
    description: 'Evaluaciones, aprobaciones y riesgos',
    icon: ClipboardSignature
  }
];

const defaultReview = {
  status: 'pendiente',
  reviewerId: null,
  date: null,
  note: ''
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('es-CL');
};

const enrichSupplier = (supplier, purchaseOrders) => {
  if (!supplier) return null;

  const categories = toArray(supplier.categories);
  const subcategories = toArray(supplier.subcategories);
  const contacts = normalizeContacts(supplier.contacts);
  const relatedOrders = Array.isArray(purchaseOrders)
    ? purchaseOrders.filter((order) => order.supplierId === supplier.id)
    : [];

  const totalCommitted = relatedOrders.reduce(
    (sum, order) => sum + Number(order.totalValue || 0),
    0
  );
  const totalInvoiced = relatedOrders.reduce(
    (sum, order) => sum + Number(order.invoicedValue || 0),
    0
  );
  const openOrders = relatedOrders.filter(
    (order) => !['recibida', 'aprobada', 'rechazada'].includes(order.status)
  );
  const lastOrderDate = relatedOrders.reduce((latest, order) => {
    if (!order.createdDate) return latest;
    if (!latest) return order.createdDate;
    return order.createdDate > latest ? order.createdDate : latest;
  }, null);

  const averageHistoryScore =
    supplier.history && supplier.history.length > 0
      ? supplier.history.reduce((sum, entry) => sum + Number(entry.evaluacion || 0), 0) /
        supplier.history.length
      : null;

  return {
    ...supplier,
    categories,
    subcategories,
    contacts,
    orders: relatedOrders,
    metrics: {
      totalCommitted,
      totalInvoiced,
      ordersCount: relatedOrders.length,
      openOrders: openOrders.length,
      lastOrderDate,
      averageHistoryScore
    }
  };
};

const Suppliers = () => {
  const {
    suppliers,
    purchaseOrders,
    updateSupplierCompliance,
    uploadSupplierDocument,
    importSuppliersFromCsv,
    addSupplier,
    evaluateSupplier,
    reviewSupplier,
    currentUser,
    findUserById
  } = useAppContext();

  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [evaluationSaved, setEvaluationSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('gestion');

  const role = currentUser?.role || 'comprador';
  const isSuperUser = role === 'super_user';
  const isLegalAdvisor = role === 'abogado' || isSuperUser;
  const isContractManager = role === 'contract_manager' || isLegalAdvisor || isSuperUser;
  const isPurchasingManager = role === 'purchasing_manager' || isSuperUser;
  const canManageCompliance = ['contract_manager', 'purchasing_manager', 'super_user', 'abogado'].includes(role);
  const canReviewDocuments = isContractManager;
  const canEvaluate = ['contract_manager', 'purchasing_manager', 'super_user', 'abogado'].includes(role);
  const canApproveSuppliers = isPurchasingManager;
  const canCreateSuppliers = ['comprador', 'purchasing_manager', 'super_user'].includes(role);
  const allowedRoles = useMemo(
    () => new Set(['purchasing_manager', 'comprador', 'contract_manager', 'super_user', 'abogado']),
    []
  );

  const normalizedSuppliers = useMemo(
    () =>
      suppliers
        .map((supplier) => enrichSupplier(supplier, purchaseOrders))
        .filter((supplier) => supplier !== null),
    [suppliers, purchaseOrders]
  );

  useEffect(() => {
    if (normalizedSuppliers.length === 0) {
      setSelectedSupplierId(null);
      return;
    }

    if (
      !selectedSupplierId ||
      !normalizedSuppliers.some((supplier) => supplier.id === selectedSupplierId)
    ) {
      setSelectedSupplierId(normalizedSuppliers[0].id);
    }
  }, [normalizedSuppliers, selectedSupplierId]);

  const selectedSupplier =
    normalizedSuppliers.find((supplier) => supplier.id === selectedSupplierId) || null;

  const supplierReviews = selectedSupplier?.reviews || { contract: defaultReview, purchasing: defaultReview };
  const contractReview = supplierReviews.contract || defaultReview;
  const purchasingReview = supplierReviews.purchasing || defaultReview;
  const canFinalizeApproval =
    isPurchasingManager &&
    contractReview.status === 'aprobado' &&
    selectedSupplier?.status !== 'Approved';
  const hasFinalDecision = ['Approved', 'Rejected'].includes(selectedSupplier?.status);
  const contractReviewer = contractReview.reviewerId
    ? findUserById(contractReview.reviewerId)
    : null;
  const purchasingReviewer = purchasingReview.reviewerId
    ? findUserById(purchasingReview.reviewerId)
    : null;

  const [riskLevel, setRiskLevel] = useState(selectedSupplier?.riskLevel || 'Medio');
  const [complianceScore, setComplianceScore] = useState(
    selectedSupplier?.complianceScore || 0
  );

  const [evaluationForm, setEvaluationForm] = useState({
    viability: selectedSupplier?.evaluation?.viability || 'pendiente',
    score:
      selectedSupplier?.evaluation?.score ??
      selectedSupplier?.complianceScore ??
      0,
    strengths: selectedSupplier?.evaluation?.strengths || '',
    risks: selectedSupplier?.evaluation?.risks || '',
    comments: selectedSupplier?.evaluation?.comments || '',
    nextReview: selectedSupplier?.evaluation?.nextReview || ''
  });

  const [documentNote, setDocumentNote] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionFeedback, setDecisionFeedback] = useState(null);

  useEffect(() => {
    setRiskLevel(selectedSupplier?.riskLevel || 'Medio');
    setComplianceScore(selectedSupplier?.complianceScore || 0);
    setEvaluationForm({
      viability: selectedSupplier?.evaluation?.viability || 'pendiente',
      score:
        selectedSupplier?.evaluation?.score ??
        selectedSupplier?.complianceScore ??
        0,
      strengths: selectedSupplier?.evaluation?.strengths || '',
      risks: selectedSupplier?.evaluation?.risks || '',
      comments: selectedSupplier?.evaluation?.comments || '',
      nextReview: selectedSupplier?.evaluation?.nextReview || ''
    });
    setEvaluationSaved(false);
    setDocumentNote('');
    setDecisionNote('');
    setDecisionFeedback(null);
  }, [selectedSupplier]);

  const summaryCounters = useMemo(() => {
    return normalizedSuppliers.reduce(
      (acc, supplier) => {
        acc.total += 1;
        acc.risk[supplier.riskLevel] = (acc.risk[supplier.riskLevel] || 0) + 1;
        acc.viability[supplier.evaluation?.viability || 'pendiente'] =
          (acc.viability[supplier.evaluation?.viability || 'pendiente'] || 0) + 1;
        return acc;
      },
      {
        total: 0,
        risk: { Bajo: 0, Medio: 0, Alto: 0 },
        viability: { aprobado: 0, condicionado: 0, rechazado: 0, pendiente: 0 }
      }
    );
  }, [normalizedSuppliers]);

  const handleRiskChange = (value) => {
    if (!selectedSupplier || !canManageCompliance) return;
    setRiskLevel(value);
    updateSupplierCompliance(selectedSupplier.id, {
      riskLevel: value,
      complianceScore
    });
  };

  const handleComplianceScoreChange = (value) => {
    if (!selectedSupplier || !canManageCompliance) return;
    const numeric = Number(value);
    setComplianceScore(numeric);
    updateSupplierCompliance(selectedSupplier.id, {
      complianceScore: numeric,
      riskLevel
    });
  };

  const handleChecklistToggle = (supplierId, key, value) => {
    if (!canManageCompliance) return;
    updateSupplierCompliance(supplierId, {
      checklistUpdates: {
        [key]: value
      }
    });
};

  const handleDocumentUpload = (supplierId, event) => {
    if (!canManageCompliance) return;
    const file = event.target.files?.[0];
    if (!file) return;
    uploadSupplierDocument(supplierId, file.name);
    event.target.value = '';
  };

  const handleImportCsv = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const text = loadEvent.target?.result;
      if (typeof text === 'string') {
        const rows = parseCsv(text);
        if (rows.length) {
          importSuppliersFromCsv(rows);
        }
      }
    };
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const handleAddSupplier = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const payload = Object.fromEntries(formData.entries());

    const supplierData = {
      name: payload.name,
      rut: payload.rut || '',
      country: payload.country || 'Chile',
      type: payload.type || 'nacional',
      categories: toArray(payload.categories),
      subcategories: toArray(payload.subcategories),
      complianceScore: Number(payload.complianceScore || 60),
      riskLevel: payload.riskLevel || 'Medio',
      legal: {
        razonSocial: payload.legalName || payload.name,
        fechaConstitucion: payload.incorporation || '',
        giro: payload.businessLine || ''
      },
      administrative: {
        contacto: payload.contact || '',
        correo: payload.email || '',
        direccion: payload.address || '',
        pais: payload.country || 'Chile',
        telefono: payload.phone || ''
      },
      financial: {
        tipoEmpresa: payload.companyType || '',
        capital: Number(payload.capital || 0),
        deudasPublicas: payload.debts || 'Sin información'
      },
      documents: [],
      history: []
    };

    addSupplier(supplierData);
    setShowAddSupplierModal(false);
    event.target.reset();
  };

  const handleEvaluationInput = (field, value) => {
    if (!canEvaluate) return;
    setEvaluationForm((prev) => ({
      ...prev,
      [field]: value
    }));
    setEvaluationSaved(false);
  };

  const handleEvaluationSubmit = (event) => {
    event.preventDefault();
    if (!selectedSupplier || !canEvaluate) return;
    evaluateSupplier(selectedSupplier.id, {
      viability: evaluationForm.viability,
      score: Number(evaluationForm.score || 0),
      strengths: evaluationForm.strengths,
      risks: evaluationForm.risks,
      comments: evaluationForm.comments,
      nextReview: evaluationForm.nextReview
    });
    setEvaluationSaved(true);
  };

  const handleDocumentDecision = (decision) => {
    if (!selectedSupplier || !canReviewDocuments) return;
    const trimmedNote = documentNote.trim();
    reviewSupplier(selectedSupplier.id, {
      stage: 'contract',
      decision,
      note: trimmedNote
    });
    setDecisionFeedback({
      type: decision === 'approved' ? 'contract-approved' : 'contract-rejected',
      message:
        decision === 'approved'
          ? t('Documentación legal validada.')
          : t('Documentación legal rechazada.')
    });
    setDocumentNote('');
  };

  const handleSupplierDecision = (decision) => {
    if (!selectedSupplier || !canApproveSuppliers) return;
    const trimmedNote = decisionNote.trim();
    const decisionViability = decision === 'approved' ? 'aprobado' : 'rechazado';

    setEvaluationForm((prev) => ({
      ...prev,
      viability: decisionViability
    }));

    evaluateSupplier(selectedSupplier.id, {
      viability: decisionViability,
      score: Number(evaluationForm.score || 0),
      strengths: evaluationForm.strengths,
      risks: evaluationForm.risks,
      comments: evaluationForm.comments,
      nextReview: evaluationForm.nextReview
    });
    reviewSupplier(selectedSupplier.id, { stage: 'purchasing', decision, note: trimmedNote });
    setDecisionFeedback({
      type: decision === 'approved' ? 'purchasing-approved' : 'purchasing-rejected',
      message:
        decision === 'approved'
          ? t('Proveedor aprobado correctamente.')
          : t('Proveedor rechazado.')
    });
    setEvaluationSaved(true);
    setDecisionNote('');
  };

  const evaluationReviewer =
    selectedSupplier?.evaluation?.reviewerId
      ? findUserById(selectedSupplier.evaluation.reviewerId)
      : null;
  const approvalReviewer =
    purchasingReviewer || (selectedSupplier?.lastReviewedBy ? findUserById(selectedSupplier.lastReviewedBy) : null);

  if (!allowedRoles.has(role)) {
    return (<div className="bg-white border border-slate-200 rounded-3xl p-10 text-center text-sm text-slate-500">{t('No cuentas con permisos para acceder a la gestión de proveedores.')}</div>);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Gestión y Homologación de Proveedores')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Evalúa riesgos, cumplimiento normativo y desempeño operativo antes de autorizar nuevos proveedores estratégicos.'
          )}</p>
        </div>
        {(canManageCompliance || canCreateSuppliers) && (
          <div className="flex flex-wrap items-center gap-2">
            {canManageCompliance && (
              <label
                htmlFor="import-suppliers"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded cursor-pointer hover:bg-neutral-50 transition-colors"
              >
                <FilePlus className="w-3.5 h-3.5" />{t('Importar CSV')}<input
                  id="import-suppliers"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleImportCsv}
                />
              </label>
            )}
            {canCreateSuppliers && (
              <Button variant="primary" icon={Building2} onClick={() => setShowAddSupplierModal(true)}>{t('Nuevo proveedor')}</Button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {SECTION_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 border text-left transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 border-brand-300'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-brand-200 hover:text-brand-600'
                }`}
              >
                <Icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-brand-600' : 'text-neutral-400'}`} />
                <span>
                  <p className="text-sm font-semibold">{t(tab.label)}</p>
                  <p className={`text-xs ${isActive ? 'text-brand-600/80' : 'text-slate-400'}`}>
                    {t(tab.description)}
                  </p>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Proveedores')}</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{t(summaryCounters.total)}</p>
          <p className="text-xs text-slate-500 mt-1">{t('Registrados en la base de homologación')}</p>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Riesgo bajo')}</p>
          <p className="mt-2 text-2xl font-semibold text-success-700">
            {t(summaryCounters.risk.Bajo)}
          </p>
          <p className="text-xs text-slate-500 mt-1">{t('Con cumplimiento alto y buen historial')}</p>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Condicionados')}</p>
          <p className="mt-2 text-2xl font-semibold text-warning-700">
            {t(summaryCounters.viability.condicionado)}
          </p>
          <p className="text-xs text-slate-500 mt-1">{t('Requieren planes de acción o seguimiento')}</p>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Rechazados')}</p>
          <p className="mt-2 text-2xl font-semibold text-danger-700">
            {t(summaryCounters.viability.rechazado)}
          </p>
          <p className="text-xs text-slate-500 mt-1">{t('No autorizados para contratación')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest">{t('Proveedores')}</h2>
          <div className="space-y-3">
            {normalizedSuppliers.map((supplier) => {
              const isActive = selectedSupplierId === supplier.id;
              return (
                <button
                  key={supplier.id}
                  onClick={() => setSelectedSupplierId(supplier.id)}
                  className={`w-full text-left p-4 border transition-colors ${
                    isActive
                      ? 'border-brand-300 bg-brand-50'
                      : 'border-slate-200 bg-white hover:border-brand-200 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t(supplier.name)}</p>
                      <p className="text-xs text-slate-500">
                        {t(supplier.legal?.giro) || t('Sin giro registrado')}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-xs font-semibold text-slate-600">
                      <Award className="w-3 h-3" />
                      {t(supplier.complianceScore)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                    <Badge tone={riskTones[supplier.riskLevel] || 'neutral'}>
                      <AlertTriangle className="w-3 h-3" />{t('Riesgo')} {t(supplier.riskLevel)}
                    </Badge>
                    <Badge tone={viabilityTones[supplier.evaluation?.viability || 'pendiente']}>
                      <ShieldCheck className="w-3 h-3" />
                      {t(viabilityLabels[supplier.evaluation?.viability || 'pendiente'])}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <TrendingUp className="w-3 h-3" />
                    <span>
                      {formatCurrency(supplier.metrics.totalInvoiced)} {t('facturado ·')}{' '}
                      {t(supplier.metrics.ordersCount)}{t('OC')}</span>
                  </div>
                </button>
              );
            })}
            {normalizedSuppliers.length === 0 && (
              <div className="border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">{t('Aún no se han cargado proveedores. Importa un CSV o registra uno nuevo.')}</div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {selectedSupplier ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Score de compliance')}</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-900">
                    {t(selectedSupplier.complianceScore)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{t('Evaluación documental y normativa más reciente.')}</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Facturado')}</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatCurrency(selectedSupplier.metrics.totalInvoiced)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{t('Monto facturado histórico en órdenes de compra.')}</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Órdenes activas')}</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">
                    {t(selectedSupplier.metrics.openOrders)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{t('Órdenes en curso o pendientes de aprobación/recepción.')}</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-widest">{t('Última orden')}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {selectedSupplier.metrics.lastOrderDate
                      ? formatDate(selectedSupplier.metrics.lastOrderDate)
                      : t('Sin registros')}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{t('Fecha de creación de la última orden de compra asociada.')}</p>
                </div>
              </div>

              {activeSection === 'gestion' && (
                <>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-white border border-slate-200 p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-neutral-400" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('Información legal y administrativa')}</p>
                      <p className="text-xs text-slate-500">{t('Reseña corporativa y datos de contacto del proveedor.')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Razón social')}</p>
                      <p>{t(selectedSupplier.legal?.razonSocial) || t('Sin información')}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Rut / Tax ID')}</p>
                      <p>{t(selectedSupplier.rut) || t('Sin información')}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Fecha constitución')}</p>
                      <p>
                        {selectedSupplier.legal?.fechaConstitucion
                          ? formatDate(selectedSupplier.legal.fechaConstitucion)
                          : t('Sin información')}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Giro principal')}</p>
                      <p>{t(selectedSupplier.legal?.giro) || t('Sin información')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Contacto')}</p>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span>{t(selectedSupplier.administrative?.contacto) || t('No registrado')}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Correo electrónico')}</p>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{t(selectedSupplier.administrative?.correo) || t('No registrado')}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Teléfono')}</p>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{t(selectedSupplier.administrative?.telefono) || t('No registrado')}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Dirección')}</p>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1 text-slate-400" />
                        <span>
                          {t(selectedSupplier.administrative?.direccion) || t('No registrada')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Tipo de empresa')}</p>
                      <p>{t(selectedSupplier.financial?.tipoEmpresa) || t('Sin información')}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Capital declarado')}</p>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span>
                          {selectedSupplier.financial?.capital
                            ? formatCurrency(selectedSupplier.financial.capital)
                            : t('Sin información')}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Deudas públicas / Comentarios')}</p>
                      <p>{t(selectedSupplier.financial?.deudasPublicas) || t('Sin información')}</p>
                    </div>
                  </div>
                  {selectedSupplier.categories.length > 0 && (
                    <div className="space-y-1 text-sm text-slate-600">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Partidas de construcción')}</p>
                      <p>{selectedSupplier.categories.join(', ')}</p>
                    </div>
                  )}
                  {selectedSupplier.subcategories.length > 0 && (
                    <div className="space-y-1 text-sm text-slate-600">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Subcategorías')}</p>
                      <p>{selectedSupplier.subcategories.join(', ')}</p>
                    </div>
                  )}
                  {selectedSupplier.balanceLastTwoYears.length > 0 && (
                    <div className="space-y-1 text-sm text-slate-600">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Balance últimos 2 años')}</p>
                      <ul className="space-y-1">
                        {selectedSupplier.balanceLastTwoYears.map((entry) => (
                          <li key={entry.id || entry.year}>
                            <span className="font-medium text-slate-700">{t(entry.year)}:</span>{' '}
                            {formatCurrency(entry.amount || 0)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <ClipboardCheck className="w-5 h-5 text-neutral-400" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{t('Historial de trabajos')}</p>
                          <p className="text-xs text-slate-500">{t('Evaluación de desempeño y retroalimentación en proyectos anteriores.')}</p>
                        </div>
                      </div>
                      {selectedSupplier.history.length === 0 ? (
                        <p className="text-sm text-slate-500">{t('Este proveedor no tiene registros de desempeño asociados todavía.')}</p>
                      ) : (
                        <div className="space-y-3">
                          {selectedSupplier.history.map((entry) => (
                            <div
                              key={entry.id}
                              className="border border-slate-200 rounded-2xl px-4 py-3 bg-slate-50"
                            >
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-semibold text-slate-800">{t(entry.oc)}</span>
                                <span className="text-xs text-slate-500">
                                  {formatDate(entry.timestamp || entry.fecha || '')}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                {t(entry.cumplimiento) || t('Sin observaciones')}
                              </p>
                              <div className="mt-2 text-xs text-slate-500">{t('Evaluación:')}{' '}
                                <span className="font-semibold text-slate-700">
                                  {entry.evaluacion != null ? `${entry.evaluacion}/5` : t('No evaluado')}
                                </span>
                              </div>
                              {entry.note && (
                                <p className="text-xs text-slate-500 mt-1">{t(entry.note)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-white border border-slate-200 p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-5 h-5 text-neutral-400" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{t('Órdenes y facturación')}</p>
                          <p className="text-xs text-slate-500">{t('Resumen de órdenes de compra vinculadas al proveedor.')}</p>
                        </div>
                      </div>
                      {selectedSupplier.orders.length === 0 ? (
                        <p className="text-sm text-slate-500">{t('Aún no existen órdenes de compra asociadas a este proveedor.')}</p>
                      ) : (
                        <div className="space-y-2">
                          {selectedSupplier.orders.slice(0, 6).map((order) => (
                            <div
                              key={order.id}
                              className="grid grid-cols-[1fr_auto] gap-2 px-3 py-3 border border-slate-100 rounded-2xl bg-slate-50 text-xs text-slate-600"
                            >
                              <div>
                                <p className="font-semibold text-slate-800">{t(order.id)}</p>
                                <p>{t(order.status)}</p>
                                <p className="text-slate-400 mt-1">{t('Creada:')} {formatDate(order.createdDate)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">
                                  {formatCurrency(order.totalValue || 0)}
                                </p>
                                <p>{t('Facturado:')} {formatCurrency(order.invoicedValue || 0)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeSection === 'compliance' && (
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white border border-slate-200 p-6 space-y-6">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{t('Matriz de cumplimiento')}</p>
                        <p className="text-xs text-slate-500">{t('Checklist normativo, certificaciones y documentación de soporte.')}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-500 uppercase">{t('Revisión Contract Manager')}</p>
                        <Badge tone={reviewStatusTones[contractReview.status] || reviewStatusTones.pendiente}>
                          {t(reviewStatusLabels[contractReview.status]) || t('Pendiente')}
                        </Badge>
                        <p className="text-[11px] text-slate-500">
                          {contractReview.date ? t('Actualizado el {0}', formatDateTime(contractReview.date)) : t('Sin revisión registrada')}
                        </p>
                        {contractReviewer && (
                          <p className="text-[11px] text-slate-500">{t('Por')} {t(contractReviewer.name)}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-500 uppercase">{t('Aprobación Gerencia de Compras')}</p>
                        <Badge tone={reviewStatusTones[purchasingReview.status] || reviewStatusTones.pendiente}>
                          {t(reviewStatusLabels[purchasingReview.status]) || t('Pendiente')}
                        </Badge>
                        <p className="text-[11px] text-slate-500">
                          {purchasingReview.date ? t('Actualizado el {0}', formatDateTime(purchasingReview.date)) : t('Sin aprobación registrada')}
                        </p>
                        {purchasingReviewer && (
                          <p className="text-[11px] text-slate-500">{t('Por')} {t(purchasingReviewer.name)}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <label className="text-slate-600 font-semibold">{t('Riesgo del proveedor')}</label>
                        <select
                          value={riskLevel}
                          onChange={(event) => handleRiskChange(event.target.value)}
                          disabled={!canManageCompliance}
                          className={`px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 ${
                            canManageCompliance
                              ? 'border-slate-200'
                              : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          {riskOptions.map((option) => (
                            <option key={option} value={option}>
                              {t(option)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                          <span>{t('Score documental (')}{t(complianceScore)})</span>
                          <span className="text-xs text-slate-500">{t('Ajusta según revisión de certificados')}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={complianceScore}
                          onChange={(event) => handleComplianceScoreChange(event.target.value)}
                          disabled={!canManageCompliance}
                          className={`w-full ${
                            canManageCompliance ? 'accent-brand-600' : 'opacity-50 cursor-not-allowed'
                          }`}
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      {checklistItems.map((item) => {
                        const checked = Boolean(
                          selectedSupplier.complianceChecklist?.[item.key]
                        );
                        return (
                          <label
                            key={item.key}
                            className={`flex items-start gap-3 p-3 border transition-colors ${
                              canManageCompliance
                                ? 'border-slate-200 hover:border-brand-300 cursor-pointer'
                                : 'border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) =>
                                handleChecklistToggle(
                                  selectedSupplier.id,
                                  item.key,
                                  event.target.checked
                                )
                              }
                              disabled={!canManageCompliance}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400 disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                            <span>
                              <p className="text-sm font-semibold text-slate-800">{t(item.label)}</p>
                              <p className="text-xs text-slate-500">{t(item.description)}</p>
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">{t('Documentación vigente')}</p>
                        <label
                          htmlFor={canManageCompliance ? `upload-doc-${selectedSupplier.id}` : undefined}
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold border transition-colors ${
                            canManageCompliance
                              ? 'text-slate-600 bg-slate-100 border-slate-200 cursor-pointer hover:bg-slate-200'
                              : 'text-slate-400 bg-slate-50 border-slate-200 cursor-not-allowed opacity-60 pointer-events-none'
                          }`}
                        >
                          <Upload className="w-3 h-3" />{t('Adjuntar')}<input
                            id={`upload-doc-${selectedSupplier.id}`}
                            type="file"
                            className="hidden"
                            disabled={!canManageCompliance}
                            onChange={(event) => handleDocumentUpload(selectedSupplier.id, event)}
                          />
                        </label>
                      </div>
                      <div className="space-y-2">
                        {selectedSupplier.documents.length === 0 && (
                          <p className="text-xs text-slate-500">{t('Sin documentos anexados.')}</p>
                        )}
                        {selectedSupplier.documents.map((document) => (
                          <div
                            key={document.id}
                            className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 text-xs text-slate-600"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span>{t(document.name)}</span>
                            </div>
                            <span>{formatDate(document.uploadedAt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {canReviewDocuments && (
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Observaciones validación legal')}</label>
                        <textarea
                          rows={2}
                          value={documentNote}
                          onChange={(event) => setDocumentNote(event.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                          placeholder={t('Anota hallazgos legales o requisitos pendientes.')}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button variant="primary" size="sm" icon={CheckCircle2} onClick={() => handleDocumentDecision('approved')}>{t('Documentos validados')}</Button>
                          <Button variant="danger" size="sm" icon={XCircle} onClick={() => handleDocumentDecision('rejected')}>{t('Rechazar documentación')}</Button>
                        </div>
                      </div>
                    )}

                    {selectedSupplier.certifications?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-slate-800">{t('Certificaciones')}</p>
                        <div className="space-y-2">
                          {selectedSupplier.certifications.map((certification) => (
                            <div
                              key={certification.id}
                              className="px-3 py-2 bg-success-50 text-xs text-success-700 border border-success-100"
                            >
                              <span className="font-semibold">{t(certification.name)}</span>{' '}{t('· Vigencia hasta')} {formatDate(certification.validUntil)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'homologacion' && (
                <div className="bg-white border border-slate-200 p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <ClipboardSignature className="w-5 h-5 text-neutral-400" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('Homologación y evaluación integral')}</p>
                      <p className="text-xs text-slate-500">{t(
                        'Define si el proveedor es viable, registra fortalezas y riesgos detectados.'
                      )}</p>
                    </div>
                  </div>
                  <form className="space-y-4" onSubmit={handleEvaluationSubmit}>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <Badge tone={supplierStatusTones[selectedSupplier.status] || supplierStatusTones['Pending Approval']}>{t('Estado:')} {t(selectedSupplier.status) || t('Pending Approval')}
                      </Badge>
                      {selectedSupplier.reviewedDate && (
                        <span className="text-slate-500">{t('Revisado el')} {formatDateTime(selectedSupplier.reviewedDate)}
                        </span>
                      )}
                      {approvalReviewer && (
                        <span className="text-slate-500">{t('Por')} {t(approvalReviewer.name)}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">{t('Resultado de homologación')}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {Object.entries(viabilityLabels).map(([value, label]) => (
                          <button
                            type="button"
                            key={value}
                            disabled={!canEvaluate}
                            onClick={() => handleEvaluationInput('viability', value)}
                            className={`px-3 py-1.5 text-xs font-semibold transition-colors border ${
                              evaluationForm.viability === value
                                ? 'bg-brand-600 text-white border-brand-600'
                                : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                            } ${!canEvaluate ? 'opacity-50 cursor-not-allowed hover:bg-slate-100' : ''}`}
                          >
                            {t(label)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4 items-center">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase block mb-2">{t('Score integral (0 - 100)')}</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={evaluationForm.score}
                          onChange={(event) => handleEvaluationInput('score', event.target.value)}
                          disabled={!canEvaluate}
                          className={`w-full ${
                            canEvaluate ? 'accent-brand-600' : 'opacity-50 cursor-not-allowed'
                          }`}
                        />
                      </div>
                      <div className="text-sm text-slate-600">
                        <p className="font-semibold text-slate-800">{t('Puntaje:')} {t(evaluationForm.score)}
                        </p>
                        <p className="text-xs text-slate-500">{t('Considera desempeño, cumplimiento, solvencia y capacidad operativa.')}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Fortalezas detectadas')}</label>
                        <textarea
                          rows={3}
                          value={evaluationForm.strengths}
                          onChange={(event) => handleEvaluationInput('strengths', event.target.value)}
                          disabled={!canEvaluate}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 ${
                            !canEvaluate ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''
                          }`}
                          placeholder={t('Experiencia, certificaciones, equipo técnico...')}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Riesgos / GAPs')}</label>
                        <textarea
                          rows={3}
                          value={evaluationForm.risks}
                          onChange={(event) => handleEvaluationInput('risks', event.target.value)}
                          disabled={!canEvaluate}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 ${
                            !canEvaluate ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''
                          }`}
                          placeholder={t('Observaciones de cumplimiento, dependencia de terceros...')}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Recomendaciones / Comentarios')}</label>
                        <textarea
                          rows={3}
                          value={evaluationForm.comments}
                          onChange={(event) => handleEvaluationInput('comments', event.target.value)}
                          disabled={!canEvaluate}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 ${
                            !canEvaluate ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''
                          }`}
                          placeholder={t('Planes de acción, seguimiento sugerido, referencias...')}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Próxima revisión')}</label>
                        <input
                          type="date"
                          value={evaluationForm.nextReview || ''}
                          onChange={(event) => handleEvaluationInput('nextReview', event.target.value)}
                          disabled={!canEvaluate}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 text-sm ${
                            !canEvaluate ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''
                          }`}
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-500 uppercase">{t('Última evaluación')}</p>
                        <p>
                          {selectedSupplier.evaluation?.lastUpdated
                            ? formatDateTime(selectedSupplier.evaluation.lastUpdated)
                            : t('Sin evaluación registrada')}
                        </p>
                        {evaluationReviewer && (
                          <p className="text-xs text-slate-500">{t('Evaluado por')} {t(evaluationReviewer.name)}· {t(evaluationReviewer.role)}
                          </p>
                        )}
                      </div>
                    </div>

                    {canApproveSuppliers && (
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase">{t('Observaciones de revisión')}</label>
                        <textarea
                          rows={3}
                          value={decisionNote}
                          onChange={(event) => setDecisionNote(event.target.value)}
                          disabled={!canApproveSuppliers || hasFinalDecision}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500 ${
                            !canApproveSuppliers || hasFinalDecision
                              ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                              : ''
                          }`}
                          placeholder={t('Comentarios finales para la homologación.')}
                        />
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {evaluationSaved && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-700">
                            <CheckCircle2 className="w-4 h-4" />{t('Evaluación guardada correctamente.')}</span>
                        )}
                        {decisionFeedback && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-semibold ${
                              decisionFeedback.type?.includes('rejected')
                                ? 'text-danger-700'
                                : 'text-success-700'
                            }`}
                          >
                            <ShieldCheck className="w-4 h-4" />
                            {t(decisionFeedback.message)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        {canApproveSuppliers && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              variant="primary"
                              icon={CheckCircle2}
                              disabled={!canFinalizeApproval || hasFinalDecision}
                              disabledReason="Requiere validación legal aprobada para confirmar."
                              onClick={() => handleSupplierDecision('approved')}
                            >{t('Aprobar proveedor')}</Button>
                            <Button
                              variant="danger"
                              icon={XCircle}
                              disabled={!canApproveSuppliers || hasFinalDecision}
                              onClick={() => handleSupplierDecision('rejected')}
                            >{t('Rechazar proveedor')}</Button>
                            {!canFinalizeApproval && !hasFinalDecision && (
                              <span className="text-[11px] text-slate-500">{t('Requiere validación legal aprobada para confirmar.')}</span>
                            )}
                          </div>
                        )}
                        <Button variant="primary" icon={ShieldAlert} type="submit" disabled={!canEvaluate}>{t('Guardar evaluación')}</Button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border border-slate-200 p-10 text-center text-sm text-slate-500">{t('Selecciona un proveedor para revisar su resumen de homologación.')}</div>
          )}
        </div>
      </div>

      {showAddSupplierModal && canCreateSuppliers && (
        <div className="fixed inset-0 bg-slate-900/70 flex items-center justify-center z-50 p-6">
          <form
            className="bg-white border border-slate-100 w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 space-y-6"
            onSubmit={handleAddSupplier}
          >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{t('Registrar proveedor')}</h2>
                  <p className="text-sm text-slate-500">{t('Completa los datos base para iniciar el proceso de homologación.')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(false)}
                  className="text-sm text-slate-500 hover:text-slate-900"
                >{t('Cerrar')}</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Nombre comercial')}</label>
                  <input
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Ej: SolarTech Ltd.')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Razón social')}</label>
                  <input
                    name="legalName"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Razón social registrada')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('RUT / Tax ID')}</label>
                  <input
                    name="rut"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder="76.543.210-9"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('País')}</label>
                  <input
                    name="country"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Chile')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Tipo de proveedor')}</label>
                  <select
                    name="type"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    defaultValue="nacional"
                  >
                    <option value="nacional">{t('Nacional')}</option>
                    <option value="internacional">{t('Internacional')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Fecha constitución')}</label>
                  <input
                    type="date"
                    name="incorporation"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Giro / Actividad')}</label>
                  <input
                    name="businessLine"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Construcción de parques solares, mantenimiento industrial...')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Categorías principales')}</label>
                  <input
                    name="categories"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Materiales, Servicios eléctricos...')}
                  />
                  <p className="text-xs text-slate-400">{t('Separar múltiples con coma (,)')}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Subcategorías')}</label>
                  <input
                    name="subcategories"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Montaje, Automatización, Transporte...')}
                  />
                  <p className="text-xs text-slate-400">{t('Separar múltiples con coma (,)')}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Contacto principal')}</label>
                  <input
                    name="contact"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Nombre del ejecutivo comercial')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Correo')}</label>
                  <input
                    name="email"
                    type="email"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder="correo@proveedor.com"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Teléfono')}</label>
                  <input
                    name="phone"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder="+56 9 1234 5678"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Dirección')}</label>
                  <input
                    name="address"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Calle, número, comuna')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Tipo de empresa')}</label>
                  <input
                    name="companyType"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('S.A., Ltda., etc.')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Capital declarado (CLP)')}</label>
                  <input
                    name="capital"
                    type="number"
                    min="0"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Deudas públicas / comentarios')}</label>
                  <textarea
                    name="debts"
                    rows={2}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    placeholder={t('Observaciones referentes a Tesorería, multas u otros hallazgos.')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Score inicial')}</label>
                  <input
                    name="complianceScore"
                    type="number"
                    min="0"
                    max="100"
                    defaultValue="60"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Riesgo inicial')}</label>
                  <select
                    name="riskLevel"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-500"
                    defaultValue="Medio"
                  >
                    {riskOptions.map((option) => (
                      <option key={option} value={option}>
                        {t(option)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setShowAddSupplierModal(false)}>{t('Cancelar')}</Button>
                <Button variant="primary" type="submit">{t('Guardar proveedor')}</Button>
              </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
